import type { ApiClient } from '../client';
import type {
  ActivePickupPinResponse,
  ApiResponse,
  ParentChildrenResponse,
  ParentNoticesResponse,
  ParentPortalDashboardResponse,
  ParentStudentDownloadsResponse,
  ParentTransportLiveResponse,
  PortalAcademicProgressResponse,
  PortalAttendanceSummary,
  PortalClassHighestResponse,
  PortalFeesSummary,
  PortalReportAnalysisResponse,
  StudentDownloadsResponse,
  StudentNoticesResponse,
  StudentPortalDashboardResponse,
  StudentPortalProfile,
  Uuid,
} from '../types';

function queryString(params: Record<string, string | undefined>): string {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value != null && value.trim()) qs.set(key, value.trim());
  });
  const encoded = qs.toString();
  return encoded ? `?${encoded}` : '';
}

export function bindPortal(client: ApiClient) {
  return {
    async getStudentPortalDashboard(): Promise<ApiResponse<StudentPortalDashboardResponse>> {
      return client.request<StudentPortalDashboardResponse>('/portal/student/dashboard');
    },

    async getStudentPortalProfile(): Promise<ApiResponse<StudentPortalProfile>> {
      return client.request<StudentPortalProfile>('/portal/student/profile');
    },

    async getStudentPortalFees(): Promise<ApiResponse<PortalFeesSummary>> {
      return client.request<PortalFeesSummary>('/portal/student/fees');
    },

    async getStudentPortalAttendance(from?: string, to?: string): Promise<ApiResponse<PortalAttendanceSummary>> {
      return client.request<PortalAttendanceSummary>(
        `/portal/student/attendance${queryString({ from, to })}`
      );
    },

    async getStudentPortalNotices(limit = 20): Promise<ApiResponse<StudentNoticesResponse>> {
      return client.request<StudentNoticesResponse>(`/portal/student/notices${queryString({ limit: String(limit) })}`);
    },

    async getStudentPortalDownloads(): Promise<ApiResponse<StudentDownloadsResponse>> {
      return client.request<StudentDownloadsResponse>('/portal/student/downloads');
    },

    async getParentPortalDashboard(): Promise<ApiResponse<ParentPortalDashboardResponse>> {
      return client.request<ParentPortalDashboardResponse>('/portal/parent/dashboard');
    },

    async getParentPortalChildren(): Promise<ApiResponse<ParentChildrenResponse>> {
      return client.request<ParentChildrenResponse>('/portal/parent/children');
    },

    async getParentPortalFees(studentId: Uuid): Promise<ApiResponse<PortalFeesSummary>> {
      return client.request<PortalFeesSummary>(
        `/portal/parent/fees${queryString({ student_id: String(studentId) })}`
      );
    },

    async getParentPortalAttendance(
      studentId: Uuid,
      from?: string,
      to?: string
    ): Promise<ApiResponse<PortalAttendanceSummary>> {
      return client.request<PortalAttendanceSummary>(
        `/portal/parent/attendance${queryString({ student_id: String(studentId), from, to })}`
      );
    },

    async getParentPortalNotices(limit = 20): Promise<ApiResponse<ParentNoticesResponse>> {
      return client.request<ParentNoticesResponse>(`/portal/parent/notices${queryString({ limit: String(limit) })}`);
    },

    async getParentPortalDownloads(studentId: Uuid): Promise<ApiResponse<ParentStudentDownloadsResponse>> {
      return client.request<ParentStudentDownloadsResponse>(
        `/portal/parent/downloads${queryString({ student_id: String(studentId) })}`
      );
    },

    /**
     * Live bus position for one linked child.
     * Backend must authorize guardian↔student, return only that child's allocation + last GPS,
     * and treat stale updated_at (~5 min) as offline (UI also soft-checks age).
     */
    async getParentPortalTransportLive(studentId: Uuid): Promise<ApiResponse<ParentTransportLiveResponse>> {
      return client.request<ParentTransportLiveResponse>(
        `/portal/parent/transport/live${queryString({ student_id: String(studentId) })}`
      );
    },

    /** Today's gate pickup PIN/QR for the logged-in student. */
    async getStudentPortalPickupPin(): Promise<ApiResponse<ActivePickupPinResponse>> {
      return client.request<ActivePickupPinResponse>('/portal/student/pickup-pin');
    },

    /** Today's gate pickup PIN/QR for a linked child. */
    async getParentPortalPickupPin(studentId: Uuid): Promise<ApiResponse<ActivePickupPinResponse>> {
      return client.request<ActivePickupPinResponse>(
        `/portal/parent/pickup-pin${queryString({ student_id: String(studentId) })}`
      );
    },

    /** Published exam % series for growth charts (student portal). */
    async getStudentPortalAcademicProgress(): Promise<ApiResponse<PortalAcademicProgressResponse>> {
      return client.request<PortalAcademicProgressResponse>('/portal/student/academic-progress');
    },

    /** Published exam % series for growth charts (guardian portal). */
    async getParentPortalAcademicProgress(
      studentId: Uuid
    ): Promise<ApiResponse<PortalAcademicProgressResponse>> {
      return client.request<PortalAcademicProgressResponse>(
        `/portal/parent/academic-progress${queryString({ student_id: String(studentId) })}`
      );
    },

    /** AI / plain-language report analysis for the logged-in student. */
    async getStudentPortalReportAnalysis(): Promise<ApiResponse<PortalReportAnalysisResponse>> {
      return client.request<PortalReportAnalysisResponse>('/portal/student/report-analysis');
    },

    /** AI / plain-language report analysis for a linked child. */
    async getParentPortalReportAnalysis(
      studentId: Uuid
    ): Promise<ApiResponse<PortalReportAnalysisResponse>> {
      return client.request<PortalReportAnalysisResponse>(
        `/portal/parent/report-analysis${queryString({ student_id: String(studentId) })}`
      );
    },

    /**
     * Highest marks by subject + overall total for the logged-in student's class.
     * Values are anonymized (marks only, no topper names).
     */
    async getStudentPortalClassHighest(): Promise<ApiResponse<PortalClassHighestResponse>> {
      return client.request<PortalClassHighestResponse>('/portal/student/class-highest');
    },

    /**
     * Highest marks for each unique class among linked children.
     * Same-class siblings are deduped into one class entry.
     */
    async getParentPortalClassHighest(): Promise<ApiResponse<PortalClassHighestResponse>> {
      return client.request<PortalClassHighestResponse>('/portal/parent/class-highest');
    },
  };
}
